import type { LinkColor, LinkLineStyle, LinkMarkStyle } from '@model-framework/link';
import type { FontSize } from '@model-framework/text';
import { type DragContext, MultiSelectionRect } from '@model-framework/ui';
import type { Rect } from '@view-model/models/common/basic/Rect';
import type { ThemeColor } from '@view-model/models/common/color';
import type { TextAlign } from '@view-model/models/common/text-align';
import { MultiSelectionMenu } from '@view-model/models/sticky/ui';
import type { StickyZoneShape } from '@view-model/models/sticky/StickyZoneView';
import type { AlignType } from './components';
import { useCallback, useState } from 'react';
import { useHandler } from '@framework/hooks';
import { type Position } from '@view-model/models/common/types/ui';
import { useRightClickMenuOpenState } from '@user/pages/ViewModelPage/useRightClickMenuOpenState';
import { MultiSelectionRightClickMenu } from './components/MultiSelectionRightClickMenu';

type Props = {
  rect: Rect;
  readonly: boolean;
  isNodeSelected: boolean;
  isMultipleNodeSelected: boolean;
  isLinkSelected: boolean;
  isZoneSelected: boolean;
  currentFontSize?: FontSize;
  currentThemeColor?: ThemeColor;
  currentTextAlign?: TextAlign;
  currentZoneShape?: StickyZoneShape;
  currentLinkLineStyle?: LinkLineStyle;
  currentLinkMarkStyle?: LinkMarkStyle;
  currentLinkColor?: LinkColor;
  selectedItems: string[];
  onFontSizeSelected(fontSize: FontSize): void;
  onToggleFontStrikethrough(fontStrikethrough: boolean): void;
  onThemeColorSelected(themeColor: ThemeColor): void;
  onTextAlignSelected(textAlign: TextAlign): void;
  onZoneShapeSelected(shape: StickyZoneShape): void;
  onGroupSelectedElements(): void;
  onLinkLineStyleSelected(lineStyle: LinkLineStyle): void;
  onLinkMarkStyleSelected(markStyle: LinkMarkStyle): void;
  onReverseLinks(): void;
  onRemoveLinks(): void;
  onLinkColorSelected(color: LinkColor): void;
  onAlignSelected(alignType: AlignType, interval: number): void;
  onDragStart(id: string, e: React.MouseEvent<SVGGElement>): void;
  onDrag(context: DragContext): void;
  onDragEnd(context: DragContext): void;
};

export const MultiSelectionView: React.FC<Props> = ({
  rect,
  readonly,
  isNodeSelected,
  isMultipleNodeSelected,
  isLinkSelected,
  isZoneSelected,
  currentFontSize,
  currentThemeColor,
  currentTextAlign,
  currentZoneShape,
  currentLinkLineStyle,
  currentLinkMarkStyle,
  currentLinkColor,
  selectedItems,
  onFontSizeSelected,
  onToggleFontStrikethrough,
  onThemeColorSelected,
  onTextAlignSelected,
  onZoneShapeSelected,
  onGroupSelectedElements,
  onLinkLineStyleSelected,
  onLinkMarkStyleSelected,
  onReverseLinks,
  onRemoveLinks,
  onLinkColorSelected,
  onAlignSelected,
  onDragStart,
  onDrag,
  onDragEnd,
}: Props) => {
  const rc = rect.applyMarginKeepingCenter(10); // 付箋の外に足される選択枠分のマージンをつける

  const [fontStrikethroughForMulti, setFontStrikethroughForMulti] = useState<boolean>(false);
  const [rightClickPosition, setRightClickPosition] = useState<Position | null>(null);
  const { activeRightClickMenuIds, setActiveRightClickMenuIds } = useRightClickMenuOpenState();
  const handleToggleSelectedNodeFontStrikethrough = useHandler(() => {
    const toggledValue = !fontStrikethroughForMulti;
    onToggleFontStrikethrough(toggledValue);
    setFontStrikethroughForMulti(toggledValue);
  });

  const handleRightClick = useHandler((position: Position): void => {
    setRightClickPosition(position);
    setActiveRightClickMenuIds(selectedItems);
  });

  const handleDragStart = useCallback(
    (e: React.MouseEvent<SVGGElement>) => {
      if (readonly) return;
      if (selectedItems.length === 0) return;
      // 右クリックメニューが開きっぱなしのままドラッグできないように、ドラッグ開始時に右クリックメニューを閉じる
      setActiveRightClickMenuIds([]);
      onDragStart(selectedItems[0], e);
    },
    [onDragStart, readonly, selectedItems, setActiveRightClickMenuIds]
  );

  const isMenuOpenForSelectedItems = useCallback(() => {
    if (selectedItems.length === 0) return false;
    if (selectedItems.length !== activeRightClickMenuIds.length) return false;
    const activeRightClickMenuIdSet = new Set(activeRightClickMenuIds);
    return selectedItems.every((id) => activeRightClickMenuIdSet.has(id));
  }, [selectedItems, activeRightClickMenuIds]);

  return (
    <>
      <MultiSelectionRect
        rect={rc}
        onRightClick={handleRightClick}
        onDragStart={handleDragStart}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
      />
      {!readonly && (
        <>
          <MultiSelectionMenu
            selectionRect={rc}
            isNodeSelected={isNodeSelected}
            isMultipleNodeSelected={isMultipleNodeSelected}
            isLinkSelected={isLinkSelected}
            isZoneSelected={isZoneSelected}
            currentFontSize={currentFontSize}
            currentThemeColor={currentThemeColor}
            currentTextAlign={currentTextAlign}
            currentZoneShape={currentZoneShape}
            currentLinkLineStyle={currentLinkLineStyle}
            currentLinkMarkStyle={currentLinkMarkStyle}
            currentLinkColor={currentLinkColor}
            onFontSizeSelected={onFontSizeSelected}
            onToggleFontStrikethrough={handleToggleSelectedNodeFontStrikethrough}
            onThemeColorSelected={onThemeColorSelected}
            onTextAlignSelected={onTextAlignSelected}
            onZoneShapeSelected={onZoneShapeSelected}
            onGroupSelectedElements={onGroupSelectedElements}
            onLinkLineStyleSelected={onLinkLineStyleSelected}
            onLinkMarkStyleSelected={onLinkMarkStyleSelected}
            onReverseLinks={onReverseLinks}
            onRemoveLinks={onRemoveLinks}
            onLinkColorSelected={onLinkColorSelected}
            onAlignSelected={onAlignSelected}
          />
          <MultiSelectionRightClickMenu
            isOpen={isMenuOpenForSelectedItems()}
            mousePosition={rightClickPosition}
            isNodeSelected={isNodeSelected}
            isMultipleNodeSelected={isMultipleNodeSelected}
            isLinkSelected={isLinkSelected}
            isZoneSelected={isZoneSelected}
            onFontSizeSelected={onFontSizeSelected}
            onToggleFontStrikethrough={handleToggleSelectedNodeFontStrikethrough}
            onThemeColorSelected={onThemeColorSelected}
            onTextAlignSelected={onTextAlignSelected}
            onZoneShapeSelected={onZoneShapeSelected}
            onGroupSelectedElements={onGroupSelectedElements}
            onLinkLineStyleSelected={onLinkLineStyleSelected}
            onLinkMarkStyleSelected={onLinkMarkStyleSelected}
            onReverseLinks={onReverseLinks}
            onRemoveLinks={onRemoveLinks}
            onLinkColorSelected={onLinkColorSelected}
            onAlignSelected={onAlignSelected}
          />
        </>
      )}
    </>
  );
};
